import DriveFileRenameOutline from "@mui/icons-material/DriveFileRenameOutline";
import { Box } from "@mui/material";
import { type FC, type ReactElement, useState } from "react";
import { type FieldErrors, FormProvider, useFormContext, useWatch } from "react-hook-form";
import type { UUID } from "../../../../utils/uuid";
import {
  type ChannelName,
  isChatChannel,
  isEmailChannel,
  isTextChannel,
  isWhatsAppChannel,
} from "../../../channels/types";
import { EmailMomentForm } from "../../../email-moment/components/email-moment-form";
import {
  MomentAccordion,
  type MomentAccordionProps,
} from "../../../generic/components/moment-accordion/moment-accordion";
import { LexicalEmailMomentForm } from "../../../lexical-email-moment/email-moment-form";
import { LexicalTextMomentForm } from "../../../lexical-text-moment/text-moment-form";
import { WhatsAppMomentForm } from "../../../whatsapp-moment/components/whatsapp-moment-form";
import type { Tags } from "../../../merge-tag/types";
import type { MomentFormMoment } from "../../types/moment-form-moment";
import { ChatEditorSelect } from "./chat-editor-select";

interface MessageAccordionProps extends Pick<
  MomentAccordionProps,
  "open" | "setOpen" | "onConfirm" | "sx"
> {
  formDisabled: boolean;
  lastFormAction?: "draft" | "publish";
  tags: Tags;
  restrictedEmailFrom: UUID[];
  newsletter?: boolean;
}

export const MessageAccordion: FC<MessageAccordionProps> = ({
  open,
  setOpen,
  formDisabled,
  tags,
  lastFormAction,
  newsletter,
  restrictedEmailFrom,
  sx,
}) => {
  const [icon, setIcon] = useState<ReactElement>();

  const methods = useFormContext<MomentFormMoment>();
  const {
    getValues,
    control,
    formState: { isSubmitted, errors },
  } = methods;

  useWatch({
    control: control,
    name: ["channel", "target_channel"],
  });
  const [selectedChannel, target_channel] = getValues(["channel", "target_channel"]);

  const mergeTags = tags.filter((tag) => {
    if (!target_channel) return true;
    if (tag.version === 1) return tag.channel;

    return !tag.category_key;
  });

  const onSubmit = async (): Promise<void> => {
    if (!selectedChannel) return;

    setIcon(
      <DriveFileRenameOutline
        sx={{
          height: "45px",
          width: "45px",
          mb: 0.5,
        }}
      />,
    );

    await methods.trigger(
      selectedChannel === "whatsapp"
        ? "whatsapp_moment"
        : ["slack", "teams"].includes(selectedChannel)
          ? "chat_moment"
          : "email_moment",
    );
    await methods.trigger("design");
    await methods.trigger("email_subject");

    setOpen?.("confirmed");
  };

  if (!selectedChannel)
    return (
      <div data-testid="message-merge-canary" data-has-merge-tags={mergeTags.length > 0}></div>
    );

  const isValid = (err: FieldErrors<MomentFormMoment>): boolean => {
    return !err.design && !err.email_moment && !err.chat_moment && !err.email_subject;
  };

  return (
    <FormProvider {...methods}>
      <MomentAccordion
        id="messageAccordion"
        summaryIcon={icon}
        title="Message"
        subtitle="What would you like to say in your Communication?"
        index={4}
        open={open}
        setOpen={setOpen}
        disabled={formDisabled}
        onConfirm={onSubmit}
        sx={{ minWidth: "100%", mb: 8, ...sx }}
        valid={{
          lastFormAction,
          valid: isValid(errors),
        }}
      >
        <Box>
          <h2>Message</h2>
          Modify our template or craft a custom message using our editor. Don’t forget to use merge
          tags to make your Communication personal.
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            borderRadius: 1,
            mt: 4,
          }}
        >
          <Box width="100%">
            <Editor
              newsletter={newsletter}
              selectedChannel={selectedChannel}
              formDisabled={formDisabled}
              isSubmitted={isSubmitted}
              mergeTags={mergeTags}
              restrictedEmailFrom={restrictedEmailFrom}
            />
          </Box>
        </Box>
      </MomentAccordion>
    </FormProvider>
  );
};

const Editor: FC<{
  newsletter?: boolean;
  selectedChannel: ChannelName;
  formDisabled: boolean;
  isSubmitted: boolean;
  mergeTags: Tags;
  restrictedEmailFrom: UUID[];
}> = ({
  newsletter,
  selectedChannel,
  formDisabled,
  isSubmitted,
  mergeTags,
  restrictedEmailFrom,
}) => {
  const { getValues, control } = useFormContext<MomentFormMoment>();

  useWatch({ control: control, name: "use_lexical_editor" });
  const use_lexical_editor = getValues("use_lexical_editor");

  if (newsletter)
    return (
      <EmailMomentForm
        disabled={formDisabled}
        showValidationErrors={isSubmitted}
        mergeTags={mergeTags}
        restrictedEmailFrom={restrictedEmailFrom}
      />
    );

  if (isEmailChannel(selectedChannel)) {
    return use_lexical_editor ? (
      <LexicalEmailMomentForm
        disabled={formDisabled}
        mergeTags={mergeTags}
        showValidationErrors={isSubmitted}
        channel={selectedChannel}
        restrictedEmailFrom={restrictedEmailFrom}
      />
    ) : (
      <EmailMomentForm
        disabled={formDisabled}
        showValidationErrors={isSubmitted}
        mergeTags={mergeTags}
        restrictedEmailFrom={restrictedEmailFrom}
      />
    );
  }

  if (isChatChannel(selectedChannel))
    return (
      <ChatEditorSelect
        chatChannel={selectedChannel}
        mergeTags={mergeTags}
        disabled={formDisabled}
        isSubmitted={isSubmitted}
      />
    );

  if (isTextChannel(selectedChannel))
    return (
      <LexicalTextMomentForm
        disabled={formDisabled}
        mergeTags={mergeTags}
        showValidationErrors={isSubmitted}
      />
    );

  if (isWhatsAppChannel(selectedChannel))
    return (
      <WhatsAppMomentForm
        disabled={formDisabled}
        mergeTags={mergeTags}
        showValidationErrors={isSubmitted}
      />
    );

  return null;
};
